import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";

import { PocketsService } from "../services/pocketsService";

// Backend decides visibility: `true` only when the organization has at least
// one ACTIVE pocket with a positive balance (INTERNAL staff always `true`).
// Cached per org so the sidebar and the cards page share a single request.
// The value rarely changes (and the backend caches it for hours), so the
// in-session window is long — a full reload still revalidates.
const STALE_AFTER_MS = 10 * 60_000;

type CacheEntry = {
  visible: boolean;
  fetchedAt: number;
};

const entries = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<boolean>>();

async function ensureVisibility(key: string): Promise<boolean> {
  const existing = entries.get(key);
  if (existing && Date.now() - existing.fetchedAt < STALE_AFTER_MS) {
    return existing.visible;
  }

  let promise = inflight.get(key);
  if (!promise) {
    promise = PocketsService.fetchVisibility()
      .then((response) => {
        const visible = response.data?.visible === true;
        entries.set(key, { visible, fetchedAt: Date.now() });
        return visible;
      })
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, promise);
  }

  return promise;
}

export function usePocketsVisibility(): { visible: boolean; isLoading: boolean } {
  const { user } = useAuth();
  const key = user?.organization_id ?? user?.organization?.id ?? (user ? "self" : null);

  const [visible, setVisible] = useState<boolean>(() => (key ? (entries.get(key)?.visible ?? false) : false));
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!key) {
      /* eslint-disable react-hooks-eslint/set-state-in-effect */
      setVisible(false);
      setIsLoading(false);
      /* eslint-enable react-hooks-eslint/set-state-in-effect */
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    ensureVisibility(key)
      .then((next) => {
        if (!cancelled) setVisible(next);
      })
      .catch(() => {
        // Fail closed — the feature is being sunset, so on error we hide it
        // rather than flashing pocket UI to an organization that may not use it.
        if (!cancelled) setVisible(false);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key]);

  return { visible, isLoading };
}
